/**
 * One pharmacy's rate card.
 *
 * A pharmacy is either on the standard MT rates — the default, and what applies
 * when it has no override at all — or on its own negotiated ones. Switching
 * between the two is PIN-gated and append-only on the server, so the previous
 * card stays on the record.
 *
 * The dialog always shows what the pharmacy is on RIGHT NOW alongside the
 * standard rate, because "is this store on special terms, and by how much" is
 * the question being answered.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Skeleton,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from "@mui/material";
import API from "Config/API";
import { toast } from "react-toastify";

import { T } from "theme/tokens";

export default function PharmacyCommissionDialog({
  open,
  onClose,
  pharmacy,
  onDone,
}) {
  const pharmacyId = pharmacy?.pharmacyId;

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [mode, setMode] = useState("global");
  const [rates, setRates] = useState([]);
  const [note, setNote] = useState("");
  const [pin, setPin] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!pharmacyId) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await API.get(
        `/admin/commission/pharmacies/${pharmacyId}`
      );
      setDetail(data);
      setMode(data?.mode || "global");
      setRates(
        (data?.effective || []).map((r) => ({
          key: r.key,
          label: r.label,
          commissionPercent: String(r.commissionPercent),
          globalCommissionPercent: r.globalCommissionPercent,
        }))
      );
    } catch (err) {
      setError(
        err?.response?.data?.message || "Could not load this pharmacy's rates."
      );
    } finally {
      setLoading(false);
    }
  }, [pharmacyId]);

  useEffect(() => {
    if (!open) return;
    setNote("");
    setPin("");
    setError("");
    load();
  }, [open, load]);

  const setRate = (key, value) =>
    setRates((rows) =>
      rows.map((r) =>
        r.key === key ? { ...r, commissionPercent: value } : r
      )
    );

  const save = async () => {
    setError("");
    if (note.trim().length < 3) {
      return setError("Say why this pharmacy's rates are changing.");
    }
    if (!/^\d{4,8}$/.test(pin)) return setError("Enter the 4–8 digit PIN.");

    if (mode === "custom") {
      for (const r of rates) {
        const pct = Number(r.commissionPercent);
        if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
          return setError(`Commission for "${r.label}" must be between 0 and 100.`);
        }
      }
    }

    setSaving(true);
    try {
      const { data } = await API.post(
        `/admin/commission/pharmacies/${pharmacyId}`,
        {
          pin,
          mode,
          note: note.trim(),
          rates:
            mode === "custom"
              ? rates.map((r) => ({
                  key: r.key,
                  commissionPercent: Number(r.commissionPercent),
                }))
              : undefined,
        }
      );
      toast.success(data?.message || "Rates saved.");
      onDone?.();
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || "Could not save the rates.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 700, color: T.ink, pb: 0.5 }}>
        Commission for this pharmacy
        <Typography sx={{ fontSize: "0.82rem", color: T.muted, fontWeight: 400 }}>
          {pharmacy?.pharmacyName || pharmacy?.pharmacyId}
          {detail?.pharmacy?.medstownId ? ` · ${detail.pharmacy.medstownId}` : ""}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Stack spacing={1} sx={{ mt: 1 }}>
            <Skeleton height={44} />
            <Skeleton height={90} />
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Box>
              <Typography
                sx={{ fontSize: "0.72rem", color: T.muted, fontWeight: 700, mb: 0.5 }}
              >
                WHICH RATE CARD APPLIES
              </Typography>
              <ToggleButtonGroup
                value={mode}
                exclusive
                size="small"
                fullWidth
                onChange={(_, v) => v && setMode(v)}
              >
                <ToggleButton value="global">Standard (MT)</ToggleButton>
                <ToggleButton value="custom">Negotiated rates</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {mode === "global" ? (
              <Alert severity="info" sx={{ fontSize: 13 }}>
                This pharmacy settles at the platform rates. Changing the
                standard card changes this pharmacy too.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ fontSize: 13 }}>
                This pharmacy settles at its own rates and is no longer affected
                by changes to the standard card.
              </Alert>
            )}

            <Box
              sx={{ border: `1px solid ${T.border}`, borderRadius: 2, p: 1.5 }}
            >
              {rates.map((r, i) => {
                const pct = Number(r.commissionPercent);
                const valid = Number.isFinite(pct) && pct >= 0 && pct <= 100;
                const differs =
                  valid && pct !== r.globalCommissionPercent;

                return (
                  <Box key={r.key}>
                    {i > 0 && <Divider sx={{ my: 1 }} />}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1.5}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                          {r.label}
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem", color: T.subtle }}>
                          standard {r.globalCommissionPercent}% ·{" "}
                          {valid ? `pharmacy keeps ${100 - pct}% of MRP` : "—"}
                        </Typography>
                      </Box>

                      <TextField
                        size="small"
                        value={r.commissionPercent}
                        disabled={mode === "global"}
                        onChange={(e) =>
                          setRate(r.key, e.target.value.replace(/[^0-9.]/g, ""))
                        }
                        error={!valid}
                        inputProps={{
                          inputMode: "decimal",
                          style: { textAlign: "center", fontWeight: 700 },
                        }}
                        InputProps={{
                          endAdornment: <span style={{ color: T.muted }}>%</span>,
                        }}
                        sx={{ width: 100 }}
                      />

                      {mode === "custom" && differs && (
                        <Chip
                          size="small"
                          label={pct < r.globalCommissionPercent ? "better" : "worse"}
                          sx={{
                            height: 20,
                            fontSize: "0.62rem",
                            fontWeight: 700,
                            color: pct < r.globalCommissionPercent ? T.success : T.warning,
                            bgcolor:
                              pct < r.globalCommissionPercent
                                ? `${T.success}15`
                                : `${T.warning}15`,
                          }}
                        />
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Box>

            <TextField
              size="small"
              fullWidth
              label="Why?"
              placeholder="e.g. Onboarding incentive agreed for 3 months"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />

            <TextField
              size="small"
              fullWidth
              type="password"
              label="Admin PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
              autoComplete="off"
              inputProps={{ inputMode: "numeric" }}
            />

            {detail?.history?.length > 0 && (
              <Box>
                <Typography
                  sx={{ fontSize: "0.7rem", color: T.muted, fontWeight: 700, mb: 0.5 }}
                >
                  HISTORY
                </Typography>
                {detail.history.slice(0, 5).map((h) => (
                  <Typography
                    key={h.version}
                    sx={{ fontSize: "0.72rem", color: T.muted }}
                  >
                    v{h.version} · {h.mode === "custom" ? "negotiated" : "standard (MT)"}
                    {h.mode === "custom" && h.rates?.length
                      ? ` (${h.rates.map((r) => `${r.key} ${r.commissionPercent}%`).join(", ")})`
                      : ""}
                    {h.note ? ` — ${h.note}` : ""}
                  </Typography>
                ))}
              </Box>
            )}

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: T.muted }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || loading}
          sx={{ bgcolor: T.primary, "&:hover": { bgcolor: "#013737" } }}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
